import type { AddOnKey, DeviceSensorPayload, Entity, IsoString } from '../types/index.js';

// ─── Occupancy Color Defaults ─────────────────────────────────────────────────

export const DEFAULT_OCCUPIED_COLOR = '#22c55e';
export const DEFAULT_VACANT_COLOR = '#ef4444';

// ─── Sensor Aggregation ───────────────────────────────────────────────────────

// Deterministic mock sensor data per deviceId seed.
// TODO: Remove once WebSocket integration is live.
export function generateMockSensorData(deviceId: string): DeviceSensorPayload {
  const seed = deviceId.split('').reduce((acc, c, i) => acc + c.charCodeAt(0) * (i + 1), 0);
  return {
    temperature: 18 + (seed % 14),
    humidity: 30 + (seed % 50),
    lux: 100 + (seed % 800),
    AQI: 20 + (seed % 180),
    occupancy: seed % 2 !== 0,
    outlet1: seed % 2 === 0,
    outlet2: seed % 4 !== 0,
    lastUpdated: new Date(Date.now() - (seed % 300) * 1_000).toISOString() as IsoString,
    deviceStatus: seed % 7 === 0 ? 'offline' : 'online',
  };
}

/**
 * Returns a finite number, or a fallback sentinel when the input is null/undefined/non-finite.
 *
 * `-Infinity` is intentionally the default so invalid values are safely ignored in
 * aggregation/range computations (for example, when deriving extrema) without
 * accidentally appearing as a valid sensor reading.
 */
function safeNum(v: number | null | undefined, fallback = -Infinity): number {
  return v != null && Number.isFinite(v) ? v : fallback;
}

function formatRange(min: number, max: number, suffix: string): string {
  if (min === max) return `${min}${suffix}`;
  return `${min}–${max}${suffix}`;
}

export interface SensorAggregation {
  tempDisplay: string;
  humidityDisplay: string;
  luxDisplay: string;
  statusDisplay: string;
  occupancyDisplay: string;
  outletsDisplay: string;
  lastUpdatedDisplay: string;
  isOccupied: boolean;
}

export function aggregateSensors(
  sensors: (DeviceSensorPayload | null)[],
  temperatureUnit: '°F' | '°C',
): SensorAggregation {
  const ready = sensors.filter((s): s is DeviceSensorPayload => s !== null);
  if (ready.length === 0) {
    return {
      tempDisplay: '—',
      humidityDisplay: '—',
      luxDisplay: '—',
      statusDisplay: '—',
      occupancyDisplay: '—',
      outletsDisplay: '—',
      lastUpdatedDisplay: '—',
      isOccupied: false,
    };
  }

  const temps = ready
    .map((s) => {
      const t = safeNum(s.temperature, NaN);
      if (!Number.isFinite(t)) return NaN;
      return Math.round(temperatureUnit === '°F' ? (t * 9) / 5 + 32 : t);
    })
    .filter(Number.isFinite);

  const humidities = ready.map((s) => safeNum(s.humidity, NaN)).filter(Number.isFinite);
  const luxes = ready.map((s) => safeNum(s.lux, NaN)).filter(Number.isFinite);

  const total = ready.length;
  const onlineCount = ready.filter((s) => s.deviceStatus === 'online').length;
  const occupiedCount = ready.filter((s) => s.occupancy).length;
  const outlet1Count = ready.filter((s) => s.outlet1).length;
  const outlet2Count = ready.filter((s) => s.outlet2).length;

  const newest =
    [...ready].sort(
      (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
    )[0]?.lastUpdated ?? null;

  return {
    tempDisplay:
      temps.length > 0 ? formatRange(Math.min(...temps), Math.max(...temps), temperatureUnit) : '—',
    humidityDisplay:
      humidities.length > 0
        ? formatRange(Math.min(...humidities), Math.max(...humidities), '%')
        : '—',
    luxDisplay: luxes.length > 0 ? formatRange(Math.min(...luxes), Math.max(...luxes), '') : '—',
    statusDisplay:
      onlineCount === total
        ? 'All online'
        : onlineCount === 0
          ? 'All offline'
          : `${onlineCount}/${total} online`,
    occupancyDisplay: `${occupiedCount}/${total}`,
    isOccupied: occupiedCount > 0,
    outletsDisplay: `${outlet1Count + outlet2Count} on / ${total * 2 - outlet1Count - outlet2Count} off`,
    lastUpdatedDisplay: newest ? formatRelativeTime(newest as IsoString) : '—',
  };
}

// ─── Add-On Checks ────────────────────────────────────────────────────────────

export function hasAddOn(entity: Entity, key: AddOnKey): boolean {
  return entity.addOns?.[key] === true;
}

// ─── Sensor Formatting ────────────────────────────────────────────────────────

export function formatTemperature(
  value: number,
  unit: '°F' | '°C',
  targetUnit?: '°F' | '°C',
): string {
  const displayUnit = targetUnit ?? unit;
  let displayValue = value;

  if (unit === '°F' && displayUnit === '°C') {
    displayValue = ((value - 32) * 5) / 9;
  } else if (unit === '°C' && displayUnit === '°F') {
    displayValue = (value * 9) / 5 + 32;
  }

  return `${Math.round(displayValue)}${displayUnit}`;
}

export function formatHumidity(value: number): string {
  return `${Math.round(value)}%`;
}

export function formatLux(value: number): string {
  return `${value} lux`;
}

export function formatSensorValue(
  key: keyof DeviceSensorPayload,
  value: DeviceSensorPayload[typeof key],
  temperatureUnit: '°F' | '°C' = '°F',
): string {
  switch (key) {
    case 'temperature':
      return formatTemperature(value as number, temperatureUnit);
    case 'humidity':
      return formatHumidity(value as number);
    case 'lux':
      return formatLux(value as number);
    case 'occupancy':
      return value ? 'Occupied' : 'Vacant';
    case 'outlet1':
    case 'outlet2':
      return value ? 'ON' : 'OFF';
    case 'deviceStatus':
      return String(value);
    case 'lastUpdated':
      return formatRelativeTime(value as IsoString);
    default:
      return String(value);
  }
}

// ─── Date / Time Helpers ──────────────────────────────────────────────────────

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  let d: Date;
  const raw: unknown = iso;
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    // Firestore Timestamp that the backend didn't serialize — convert directly
    const obj = raw as Record<string, unknown>;
    const seconds =
      typeof obj._seconds === 'number'
        ? obj._seconds
        : typeof obj.seconds === 'number'
          ? obj.seconds
          : null;
    if (seconds === null) return '—';
    d = new Date(seconds * 1000);
  } else {
    d = new Date(iso);
  }
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function buildingsLabel(
  buildings: string[] | undefined,
  fallback = 'All buildings',
): string {
  if (!buildings || buildings.length === 0) return fallback;
  return `${buildings.length} building${buildings.length !== 1 ? 's' : ''}`;
}

export function formatRelativeTime(isoString: IsoString): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}
